/**
 * Units Mapping View
 * 
 * Page for mapping units across different channels.
 * Now uses module's own hooks and services.
 */

import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useToast } from '@context/ToastContext';

// Import from module hooks
import {
  useGetAirbnbUnitsQuery,
  useGetBookingUnitsQuery,
  useGetGathernUnitsQuery,
  useGetMsoolUnits,
  useMapUnits,
  useUnmapUnits,
} from '../../hooks';

// Import from module types
import { ChannelType, ChannelName, IUnitSelection, IMsoolUnit, IUnmapUnitsRequest, ILinkedUnit } from '../../types';
import { T_KEYS } from '../../constants';

// Import custom hook from module
import { useUnitsMapping } from '../../hooks/useUnitsMapping';

// Import components from original location until moved
import {
  EmptyState,
  OnboardingModal,
  MsoolUnitsCard,
  MergeButton,
  MergeConfirmModal,
  UnmapConfirmModal,
  HeaderSection,
  ChannelCardComponent,
} from '../../components/UnitsMapping';

// Import styles from module
import { PageContainer, MainContent } from '../../styles/UnitsMappingStyles';

const UnitsMappingView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Custom hook for selection management
  const {
    selectedUnits,
    isUnitSelected,
    toggleUnitSelection,
    clearSelection,
    selectedChannels,
    canMerge,
    selectionCount,
    getMapPayload,
    channelTypeToName,
  } = useUnitsMapping();

  // Modal states
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showMergeConfirmModal, setShowMergeConfirmModal] = useState(false);
  const [showUnmapConfirmModal, setShowUnmapConfirmModal] = useState(false);
  const [unmapTargetUnit, setUnmapTargetUnit] = useState<IMsoolUnit | null>(null);

  // UI states
  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set(['airbnb', 'booking', 'gathern']));
  const [searchQueries, setSearchQueries] = useState<Record<string, string>>({
    airbnb: '',
    booking: '',
    gathern: '',
  });
  const [statusFilters, setStatusFilters] = useState<Record<string, string>>({
    airbnb: 'all',
    booking: 'all',
    gathern: 'all',
  });

  // Fetch units from all channels using module hooks
  const { data: airbnbUnits, isLoading: loadingAirbnb } = useGetAirbnbUnitsQuery();
  const { data: bookingUnits, isLoading: loadingBooking } = useGetBookingUnitsQuery();
  const { data: gathernUnits, isLoading: loadingGathern } = useGetGathernUnitsQuery();
  
  // Fetch Msool mapped units using module hook
  const { data: msoolUnitsData, isLoading: loadingMsool } = useGetMsoolUnits();
  
  // Mutations using module hooks
  const { mutate: mapUnits, isPending: isMapping } = useMapUnits();
  const { mutate: unmapUnits, isPending: isUnmapping } = useUnmapUnits();

  // Check if first time user
  const hasNoMappedUnits = useMemo(() => {
    return !msoolUnitsData?.units || msoolUnitsData.units.length === 0;
  }, [msoolUnitsData]);

  // Check if onboarding modal should be shown
  useEffect(() => {
    const hasSeenOnboarding = localStorage.getItem('units_mapping_onboarding_seen');
    if (!hasSeenOnboarding && hasNoMappedUnits) {
      setShowOnboardingModal(true);
    }
  }, [hasNoMappedUnits]);

  // Combine all units data
  const allUnitsData = useMemo(() => ({
    airbnb: airbnbUnits?.units || [],
    booking: bookingUnits?.units || [],
    gathern: gathernUnits?.units || [],
  }), [airbnbUnits, bookingUnits, gathernUnits]);

  // Check if there are any units to map
  const hasUnitsToMap = useMemo(() => {
    const totalUnits = 
      (airbnbUnits?.units?.length || 0) + 
      (bookingUnits?.units?.length || 0) + 
      (gathernUnits?.units?.length || 0);
    return totalUnits > 0;
  }, [airbnbUnits, bookingUnits, gathernUnits]);

  // Loading state
  const isLoadingUnits = loadingAirbnb || loadingBooking || loadingGathern || loadingMsool;

  // Channel configuration
  const channelConfig = useMemo(() => ({
    airbnb: {
      name: t(T_KEYS.UNITS_MAPPING_AIRBNB_UNITS),
      icon: 'A',
      color: '#ff5a5f',
      bgColor: '#ff5a5f',
      channelType: ChannelType.Airbnb,
    },
    booking: {
      name: t(T_KEYS.UNITS_MAPPING_BOOKING_UNITS),
      icon: 'B',
      color: '#003580',
      bgColor: '#003580',
      channelType: ChannelType.Booking,
    },
    gathern: {
      name: t(T_KEYS.UNITS_MAPPING_GATHERN_UNITS),
      icon: 'G',
      color: '#1a73e8',
      bgColor: '#1a73e8',
      channelType: ChannelType.Gathern,
    },
  }), [t]);

  // Filter units based on search and status
  const getFilteredUnits = useCallback((channelKey: string, units: any[]) => {
    const search = searchQueries[channelKey]?.toLowerCase() || '';
    const status = statusFilters[channelKey] || 'all';

    return units.filter((unit: any) => {
      const matchesSearch = !search || 
        unit.listingName?.toLowerCase().includes(search) ||
        unit.channelListingId?.toLowerCase().includes(search);

      let matchesStatus = true;
      if (status === 'mapped') {
        matchesStatus = unit.isMapped === true;
      } else if (status === 'not_mapped') {
        matchesStatus = unit.isMapped !== true;
      }

      return matchesSearch && matchesStatus;
    });
  }, [searchQueries, statusFilters]);

  // Handlers
  const handleBackToMain = useCallback(() => {
    navigate('/channels');
  }, [navigate]);

  const handleChannelToggle = useCallback((channelId: string) => {
    setExpandedChannels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(channelId)) {
        newSet.delete(channelId);
      } else {
        newSet.add(channelId);
      }
      return newSet;
    });
  }, []);

  const handleSearchChange = useCallback((channelKey: string, query: string) => {
    setSearchQueries(prev => ({ ...prev, [channelKey]: query }));
  }, []);

  const handleStatusFilterChange = useCallback((channelKey: string, status: string) => {
    setStatusFilters(prev => ({ ...prev, [channelKey]: status }));
  }, []);

  const handleUnitToggle = useCallback((unit: any, channelKey: ChannelName) => {
    const config = channelConfig[channelKey];
    const selection: IUnitSelection = {
      unitId: unit.id,
      userChannelId: unit.userChannelId,
      channelListingId: unit.channelListingId,
      channel: channelKey,
      channelType: config.channelType,
      name: unit.listingName,
      image: unit.listingImage,
    };
    toggleUnitSelection(selection);
    
    setExpandedChannels(prev => {
      const newSet = new Set(prev);
      newSet.delete(channelKey);
      return newSet;
    });
  }, [channelConfig, toggleUnitSelection]);

  const handleOnboardingClose = useCallback(() => {
    localStorage.setItem('units_mapping_onboarding_seen', 'true');
    setShowOnboardingModal(false);
  }, []);

  const handleOnboardingStart = useCallback(() => {
    localStorage.setItem('units_mapping_onboarding_seen', 'true');
    setShowOnboardingModal(false);
  }, []);

  const handleMergeClick = useCallback(() => {
    if (canMerge) {
      setShowMergeConfirmModal(true);
    }
  }, [canMerge]);

  const handleMergeConfirm = useCallback(() => {
    const payload = getMapPayload();
    mapUnits(payload, {
      onSuccess: () => {
        showToast(t(T_KEYS.UNITS_MAPPING_SUCCESS_MERGED));
        clearSelection();
        setShowMergeConfirmModal(false);
      },
      onError: (error: any) => {
        showToast(error?.messageAr || t(T_KEYS.UNITS_MAPPING_ERROR_MERGE_FAILED));
      },
    });
  }, [getMapPayload, mapUnits, showToast, t, clearSelection]);

  const handleUnmapClick = useCallback((msoolUnit: IMsoolUnit) => {
    setUnmapTargetUnit(msoolUnit);
    setShowUnmapConfirmModal(true);
  }, []);

  const handleUnmapConfirm = useCallback(() => {
    if (!unmapTargetUnit) return;
    
    const unmapPayload: IUnmapUnitsRequest = {
      propertyId: unmapTargetUnit.propertyId,
      units: (unmapTargetUnit.channels ?? []).map((channel: ILinkedUnit) => ({
        userChannelId: channel.userChannelId,
        channelType: channel.channelType,
        channelListingId: channel.channelListingId,
      })),
    };
    
    unmapUnits(unmapPayload, {
      onSuccess: () => {
        showToast(t(T_KEYS.UNITS_MAPPING_SUCCESS_UNMAPPED));
        setShowUnmapConfirmModal(false);
        setUnmapTargetUnit(null);
      },
      onError: (error: any) => {
        showToast(error?.messageAr || t(T_KEYS.UNITS_MAPPING_ERROR_UNMAP_FAILED));
      },
    });
  }, [unmapTargetUnit, unmapUnits, showToast, t]);

  const handleStartMapping = useCallback(() => {
    setShowOnboardingModal(true);
  }, []);

  // Show loading state
  if (isLoadingUnits) {
    return (
      <PageContainer>
        <MainContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <CircularProgress sx={{ color: '#5b3fff' }} />
          </Box>
        </MainContent>
      </PageContainer>
    );
  }

  // Show empty state if no units to map
  if (!hasUnitsToMap) {
    return (
      <PageContainer>
        <MainContent>
          <HeaderSection onBackClick={handleBackToMain} />
          <EmptyState onStartMapping={handleStartMapping} />
        </MainContent>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <MainContent>
        <HeaderSection onBackClick={handleBackToMain} />
        <div>
          {msoolUnitsData?.units && msoolUnitsData.units.length > 0 && (
            <MsoolUnitsCard
              units={msoolUnitsData.units}
              isLoading={loadingMsool}
              onUnmap={handleUnmapClick}
            />
          )}

          {Object.entries(channelConfig).map(([channelKey, config]) => {
            const units = allUnitsData[channelKey as keyof typeof allUnitsData] || [];
            const filteredUnits = getFilteredUnits(channelKey, units);
            const isExpanded = expandedChannels.has(channelKey);
            const isLoading = 
              (channelKey === 'airbnb' && loadingAirbnb) ||
              (channelKey === 'booking' && loadingBooking) ||
              (channelKey === 'gathern' && loadingGathern);
            const selectedCount = selectedUnits.filter(u => u.channel === channelKey).length;

            return (
              <ChannelCardComponent
                key={channelKey}
                channelKey={channelKey}
                config={config}
                units={units}
                filteredUnits={filteredUnits}
                isExpanded={isExpanded}
                isLoading={isLoading}
                searchQuery={searchQueries[channelKey] || ''}
                statusFilter={statusFilters[channelKey] || 'all'}
                selectedCount={selectedCount}
                onToggleExpand={handleChannelToggle}
                onSearchChange={handleSearchChange}
                onStatusFilterChange={handleStatusFilterChange}
                onUnitToggle={handleUnitToggle}
                isUnitSelected={isUnitSelected}
              />
            );
          })}
        </div>
      </MainContent>

      <MergeButton
        selectionCount={selectionCount}
        canMerge={canMerge}
        onMerge={handleMergeClick}
        onClear={clearSelection}
        selectedChannelsCount={selectedChannels.length}
      />

      <OnboardingModal
        open={showOnboardingModal}
        onClose={handleOnboardingClose}
        onStart={handleOnboardingStart}
      />

      <MergeConfirmModal
        open={showMergeConfirmModal}
        onClose={() => setShowMergeConfirmModal(false)}
        onConfirm={handleMergeConfirm}
        selectedUnits={selectedUnits}
        isLoading={isMapping}
      />

      <UnmapConfirmModal
        open={showUnmapConfirmModal}
        onClose={() => {
          setShowUnmapConfirmModal(false);
          setUnmapTargetUnit(null);
        }}
        onConfirm={handleUnmapConfirm}
        isLoading={isUnmapping}
      />
    </PageContainer>
  );
};

export default UnitsMappingView;
